import { useEffect, useRef, useState } from 'react';

/**
 * The receipt camera, inside the app.
 *
 * `<input type="file" capture="environment">` opens the camera app on an
 * Android tablet, but a Surface Pro's browser treats it as a desktop and
 * opens a file picker instead — no camera at all (the owner, 23 September
 * 2026). So the app opens the camera itself with getUserMedia, asking for
 * the rear one, and takes the frame off a canvas. If the camera cannot be
 * opened (no permission, no camera, an old browser) the file picker is
 * offered as the way out, so a receipt can always be attached.
 *
 * The frame goes back as a JPEG File; the caller compresses it like any
 * other photo (`compressPhoto`, under ~300KB) and uploads it.
 */

interface Props {
  title: string;
  onCapture: (file: File) => void;
  onCancel: () => void;
}

export function cameraAvailable(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

type Facing = 'environment' | 'user';

export default function CameraCapture({ title, onCapture, onCancel }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facing, setFacing] = useState<Facing>('environment');
  const [canSwitch, setCanSwitch] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setFailed(null);
    const stop = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
    stop();
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1440 } }, audio: false })
      .then(async (stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          try { await video.play(); } catch { /* autoplay refused: the frame still shows */ }
        }
        setReady(true);
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          setCanSwitch(devices.filter((d) => d.kind === 'videoinput').length > 1);
        } catch { /* no device list: no switch button */ }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : '';
        setFailed(
          name === 'NotAllowedError'
            ? 'The browser was not allowed to use the camera. Allow it in the address bar, or choose a photo instead.'
            : 'The camera could not be opened on this device. Choose a photo instead.',
        );
      });
    return () => { cancelled = true; stop(); };
  }, [facing]);

  function take() {
    const video = videoRef.current;
    if (!video || !ready || video.videoWidth === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      onCapture(new File([blob], `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="mbox" style={{ maxWidth: 640 }}>
        <div className="mhead">{title}</div>
        <div className="sub" style={{ marginBottom: 10 }}>
          {failed ?? 'Hold the receipt flat and fill the frame.'}
        </div>

        {!failed && (
          <div style={{ background: '#000', borderRadius: 12, overflow: 'hidden', minHeight: 200 }}>
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              style={{ width: '100%', display: 'block', maxHeight: '60vh', objectFit: 'contain' }}
            />
          </div>
        )}

        {failed && (
          <label className="btn" style={{ cursor: 'pointer', display: 'block', textAlign: 'center' }}>
            Choose a photo instead
            <input
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) onCapture(file);
              }}
            />
          </label>
        )}

        <div className="bar" style={{ marginTop: 12 }}>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          {!failed && canSwitch && (
            <button type="button" className="btn" onClick={() => setFacing(facing === 'environment' ? 'user' : 'environment')}>
              Switch camera
            </button>
          )}
          {!failed && (
            <button type="button" className="btn pri" disabled={!ready} onClick={take} style={{ flex: 1 }}>
              {ready ? 'Take photo' : 'Opening camera…'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
